import { cn } from '@/lib/utils';
import { InfoTooltip, Tooltip } from '@/components/ui/Tooltip';
import { Target, Loader2, FlaskConical, Microscope } from 'lucide-react';
import { INTENSITY_CONFIGS } from './constants';
import type { StrategicIntensity, StrategicPhase } from '@/hooks/useStrategicStream';

export function StrategicHeader({ intensity, phase, isSmallScreen, effectiveIntensity, estimatedCredits }: {
  intensity: StrategicIntensity; phase: StrategicPhase; isSmallScreen: boolean;
  effectiveIntensity?: StrategicIntensity; estimatedCredits?: number;
}) {
  // Show effective intensity if it differs from requested (e.g., trial tier downgrade)
  const displayIntensity = effectiveIntensity || intensity;
  const wasDowngraded = effectiveIntensity && effectiveIntensity !== intensity;
  const intensityConfig = INTENSITY_CONFIGS[displayIntensity];
  const isActive = phase !== 'idle' && phase !== 'concluded' && phase !== 'error';

  return (
    <div className={cn('border-b border-border-subtle bg-bar-bg/50 flex-shrink-0', isSmallScreen ? 'px-3 py-3' : 'px-6 py-4')}>
      <div className="flex items-center justify-between gap-2">
        <div className={cn('flex items-center gap-1.5 sm:gap-2 font-mono text-text-muted uppercase tracking-wider', isSmallScreen ? 'text-xs' : 'text-sm')}>
          <Target className={cn('text-blue-500', isSmallScreen ? 'w-3.5 h-3.5' : 'w-4 h-4')} />
          <span className="flex items-center gap-1.5">
            Strategic Blueprint
            <InfoTooltip
              content="Strategy consulting-style analysis. Your question is split into independent research tracks (workstreams), each investigated by a specialized AI team."
              position="bottom"
            />
            <Tooltip content="This feature is in early testing. Results may be less polished than General or Financial modes." position="bottom">
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded cursor-help">
                <FlaskConical className="w-2.5 h-2.5" />
                Alpha
              </span>
            </Tooltip>
          </span>
          {isActive && (
            <span className={cn(
              'flex items-center gap-1 sm:gap-1.5 text-verdict-primary',
              isSmallScreen ? 'ml-1' : 'ml-2'
            )}>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="hidden sm:inline">Processing...</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-light/60 border border-border/50',
            isSmallScreen ? 'text-[10px]' : 'text-xs'
          )}>
            <Microscope className={cn(
              'text-verdict-primary',
              isSmallScreen ? 'w-3 h-3' : 'w-3.5 h-3.5'
            )} />
            <span className="text-text-secondary">
              {intensityConfig.label}
            </span>
            <span className="text-text-muted">|</span>
            <Tooltip content="Independent research tracks — each covers a distinct aspect (market, competition, risks) to ensure nothing is overlooked." position="bottom">
              <span className="text-text-muted cursor-help">
                {intensityConfig.workstreams} workstreams
              </span>
            </Tooltip>
            <span className="text-text-muted">|</span>
            <Tooltip content="AI models that argue for and against the strategy in a structured debate after research is complete." position="bottom">
              <span className="text-text-muted cursor-help">
                {intensityConfig.debaters} debaters
              </span>
            </Tooltip>
          </div>
          {estimatedCredits != null && estimatedCredits > 0 && (
            <Tooltip content="Estimated credit cost for this analysis" position="bottom">
              <span className={cn('px-2 py-1 rounded-lg bg-surface-light/60 border border-border/50 text-text-muted font-mono cursor-help', isSmallScreen ? 'text-[10px]' : 'text-xs')}>
                ~{estimatedCredits} credits
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      {wasDowngraded && (
        <div className={cn('mt-2 px-2 py-1 rounded text-amber-400 bg-amber-500/10 border border-amber-500/20', isSmallScreen ? 'text-[10px]' : 'text-xs')}>
          Intensity adjusted to {displayIntensity} for your current plan.{' '}
          <span className="text-amber-500/70">Upgrade to unlock {intensity} intensity.</span>
        </div>
      )}
    </div>
  );
}
